<script setup>
import { Link } from '@inertiajs/vue3';
import SpinnerIcon from '@js/components/icons/SpinnerIcon.vue';
import { computed } from 'vue';

const {
    type = 'primary',
    size = 'medium',
    submit = false,
    href,
    target = undefined,
    download = null,
    callback,
    icon,
    iconSize = null,
    leftIcon,
    rightIcon,
    loading = null,
    ping = false,
    rotateIcon = false,
    square = false,
    leftBadgeIcon,
} = defineProps({
    type: {
        type: String,
        required: false,
    },
    size: {
        type: String,
        required: false,
    },
    submit: {
        type: Boolean,
        required: false,
    },
    href: {
        type: String,
        required: false,
    },
    target: {
        type: String,
        required: false,
    },
    download: {
        type: Boolean,
        required: false,
    },
    callback: {
        type: Function,
        required: false,
    },
    icon: {
        required: false,
        default: null,
    },
    iconSize: {
        type: [String, null],
        required: false,
    },
    leftIcon: {
        required: false,
        default: null,
    },
    rightIcon: {
        required: false,
        default: null,
    },
    loading: {
        type: Boolean,
        required: false,
    },
    ping: {
        type: Boolean,
        required: false,
    },
    rotateIcon: {
        type: Boolean,
        required: false,
    },
    square: {
        type: Boolean,
        required: false,
    },
    leftBadgeIcon: {
        type: Object,
        required: false,
    },
});

const isButton = computed(() => {
    if (submit) return true;

    if (callback) {
        return true;
    }

    return true;
});

const isLink = computed(() => {
    return href && !(download || target === '_blank');
});

const isAnchor = computed(() => {
    return href && (download || target === '_blank');
});
</script>

<template>
    <component
        :is="isLink ? Link : isAnchor ? 'a' : 'button'"
        :href="href"
        :target="target"
        :download="isAnchor && download ? '' : null"
        class="relative flex items-center justify-center gap-2 overflow-visible rounded-sm duration-150 disabled:cursor-not-allowed disabled:opacity-60"
        :class="{
            'bg-indigo-600 text-white': type === 'primary',
            'enabled:hover:bg-indigo-950': type === 'primary' && isButton,
            'hover:bg-indigo-950': type === 'primary' && (isLink || isAnchor),
            'bg-gray-200 text-indigo-600': type === 'secondary',
            'enabled:hover:bg-indigo-600 enabled:hover:text-white':
                type === 'secondary' && isButton,
            'hover:bg-indigo-600 hover:text-white':
                type === 'secondary' && (isLink || isAnchor),
            'bg-red-500 text-white': type === 'danger',
            'enabled:hover:bg-red-400': type === 'danger' && isButton,
            'hover:bg-red-400': type === 'danger' && (isLink || isAnchor),
            'bg-green-500 text-white': type === 'success',
            'enabled:hover:bg-green-400': type === 'success' && isButton,
            'hover:bg-green-400': type === 'success' && (isLink || isAnchor),
            'text-indigo-600': type === 'transparent',
            'enabled:hover:underline': type === 'transparent' && isButton,
            'hover:underline': type === 'transparent' && (isLink || isAnchor),
            'border border-gray-300': type === 'bordered',
            'enabled:hover:bg-gray-300': type === 'bordered' && isButton,
            'hover:bg-gray-300': type === 'bordered' && (isLink || isAnchor),
            'px-3 py-1 text-sm': size === 'small',
            'px-4 py-2 text-sm': size === 'medium',
            'px-4 py-2 text-base': size === 'large',
            'aspect-square': square,
            'size-8': icon && size === 'small' && square,
        }"
        @click="(event) => (callback ? callback(event) : null)"
        @mousedown.middle="(event) => (callback ? callback(event) : null)"
        :disabled="loading"
        :type="submit ? 'submit' : href ? undefined : 'button'"
    >
        <template v-if="icon">
            <component
                v-if="icon && !loading"
                :is="icon"
                class="duration-150"
                :class="[
                    iconSize ?? 'size-5',
                    'shrink-0 grow-0',
                    rotateIcon ? 'rotate-180 transform' : '',
                ]"
            />

            <SpinnerIcon
                v-else-if="loading"
                class="size-5 animate-spin"
                :class="{
                    'fill-indigo-600 text-white': type === 'primary',
                }"
            />
        </template>

        <template v-else>
            <component
                v-if="leftIcon && !loading"
                :is="leftIcon"
                class="size-4"
            />

            <SpinnerIcon
                v-else-if="loading"
                class="size-4 animate-spin"
                :class="{
                    'fill-indigo-600 text-white': type === 'primary',
                }"
            />

            <slot />

            <component
                v-if="rightIcon"
                :is="rightIcon"
                class="size-4 duration-150"
                :class="{
                    'rotate-180 transform': rotateIcon,
                }"
            />
        </template>

        <span
            v-if="leftBadgeIcon"
            class="absolute -top-1.5 -left-1.5 flex size-4 items-center justify-center rounded-full border border-indigo-600 bg-gray-200 text-indigo-600"
        >
            <component :is="leftBadgeIcon" class="size-3" />
        </span>

        <span v-if="ping" class="absolute -top-1 -right-1 flex size-3">
            <span
                class="absolute inline-flex h-full w-full animate-ping rounded-full bg-yellow-400 opacity-75"
            >
            </span>
            <span
                class="relative inline-flex size-3 rounded-full bg-yellow-500"
            ></span>
        </span>
    </component>
</template>
